import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

export function getPurchaseState({ variant, quantity = 1, confirmed = false, busy = false }) {
  const minimum = Math.max(1, Number(variant?.minimum) || 1);
  const increment = Math.max(1, Number(variant?.increment) || 1);
  const maximum = Number(variant?.maximum) || Infinity;
  const requested = Number.isFinite(Number(quantity)) ? Number(quantity) : minimum;
  const bounded = Math.min(maximum, Math.max(minimum, requested));
  const normalized = minimum + Math.floor((bounded - minimum) / increment) * increment;
  const valid = Boolean(variant?.id) && Number.isFinite(Number(variant?.price));
  return {
    id: valid ? String(variant.id) : '',
    quantity: normalized,
    unitPrice: valid ? Number(variant.price) : 0,
    totalPrice: valid ? Number(variant.price) * normalized : 0,
    needsChoice: !confirmed || !valid,
    available: valid && variant.available === true,
    canPurchase: valid && confirmed && variant.available === true && !busy,
  };
}

const root = document.querySelector('.v4-prototype-shell');

if (root && root.dataset.v4PrototypeReady !== 'true') {
  root.dataset.v4PrototypeReady = 'true';
  root.querySelectorAll('[data-v4p-price-max], [data-v4p-price-min]').forEach((link) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('filter.v.price.gte');
    url.searchParams.delete('filter.v.price.lte');
    url.searchParams.delete('page');
    const maximum = link.dataset.v4pPriceMax;
    const minimum = link.dataset.v4pPriceMin;
    url.searchParams.set(maximum ? 'filter.v.price.lte' : 'filter.v.price.gte', maximum || minimum);
    link.href = url.href;
    const current = new URL(window.location.href);
    const selected = maximum
      ? current.searchParams.get('filter.v.price.lte') === maximum && !current.searchParams.has('filter.v.price.gte')
      : current.searchParams.get('filter.v.price.gte') === minimum && !current.searchParams.has('filter.v.price.lte');
    link.classList.toggle('is-active', selected);
    if (selected) link.querySelector('.v4p-filter-box').textContent = '✓';
  });
  const menu = root.querySelector('[data-v4p-mobile-menu]');
  const backdrop = root.querySelector('[data-v4p-backdrop]');
  const toast = root.querySelector('[data-v4p-toast]');
  const filterSheet = root.querySelector('[data-v4p-filter-sheet]');
  const mainProductImage = root.querySelector('#V4PdpMainImage');
  const productLightbox = root.querySelector('[data-v4p-lightbox]');
  const productLightboxImage = root.querySelector('[data-v4p-lightbox-image]');
  const productZoomButton = root.querySelector('[data-v4p-zoom-open]');
  const productForm = root.querySelector('.v4p-pdp-form');
  const variantInput = root.querySelector('[data-v4p-variant-input]');
  const quantityInput = root.querySelector('[data-v4p-quantity-input]');
  const variantButtons = [...root.querySelectorAll('[data-v4p-variant-id]')];
  const pendingForms = new WeakSet();
  let selectedVariant = variantButtons.find((button) => button.dataset.v4pVariantId === variantInput?.value) || root;
  let selectionConfirmed = root.dataset.v4pSelectionConfirmed === 'true';

  const readVariant = (element) => ({
    id: element?.dataset.v4pVariantId,
    price: element?.dataset.v4pVariantAmount,
    available: element?.dataset.v4pVariantAvailable === 'true',
    minimum: element?.dataset.v4pQuantityMin,
    maximum: element?.dataset.v4pQuantityMax,
    increment: element?.dataset.v4pQuantityStep,
  });

  const purchaseState = () => getPurchaseState({
    variant: readVariant(selectedVariant),
    quantity: quantityInput?.value,
    confirmed: selectionConfirmed,
    busy: productForm ? pendingForms.has(productForm) : false,
  });

  const renderPurchase = () => {
    if (!productForm) return;
    const state = purchaseState();
    const busy = pendingForms.has(productForm);
    const price = selectedVariant.dataset.v4pVariantPrice || '';
    const total = state.quantity === 1 ? price : new Intl.NumberFormat(root.dataset.v4pLocale, {
      style: 'currency', currency: root.dataset.v4pCurrency,
    }).format(state.totalPrice / 100);
    const label = busy ? root.dataset.v4pAddingLabel : state.needsChoice ? root.dataset.v4pChooseLabel
      : state.available ? root.dataset.v4pAddLabel : root.dataset.v4pSoldOutLabel;
    variantInput.value = state.id;
    variantInput.disabled = state.needsChoice;
    quantityInput.value = String(state.quantity);
    root.dataset.v4pSelectionConfirmed = String(selectionConfirmed);
    root.querySelectorAll('[data-v4p-selected-variant]').forEach((element) => {
      element.textContent = state.needsChoice ? root.dataset.v4pChooseLabel : selectedVariant.dataset.v4pVariantTitle;
    });
    root.querySelectorAll('[data-v4p-pdp-price]').forEach((element) => { element.textContent = price; });
    root.querySelectorAll('[data-v4p-button-price], [data-v4p-sticky-price]').forEach((element) => {
      element.textContent = total;
      element.hidden = state.needsChoice;
    });
    root.querySelectorAll('[data-v4p-purchase-label]').forEach((element) => { element.textContent = label; });
    root.querySelectorAll('[data-v4p-quantity], [data-v4p-sticky-quantity]').forEach((element) => {
      element.textContent = String(state.quantity);
    });
    root.querySelectorAll('[data-v4p-sticky-submit], .v4p-pdp-form button[type="submit"]').forEach((button) => {
      button.disabled = busy || (!state.needsChoice && !state.available);
      button.setAttribute('aria-busy', String(busy));
    });
    variantButtons.forEach((button) => {
      const active = selectionConfirmed && button === selectedVariant;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      button.disabled = busy;
    });
    root.querySelectorAll('[data-v4p-quantity-minus], [data-v4p-quantity-plus]').forEach((button) => { button.disabled = busy; });
    const accelerated = root.querySelector('.v4p-dynamic-checkout');
    if (accelerated) accelerated.hidden = !state.canPurchase;
    const restock = root.querySelector('.pdp-restock');
    if (restock) restock.hidden = state.needsChoice || state.available;
    const restockBody = restock?.querySelector('[name="contact[body]"]');
    if (restockBody) restockBody.value = `${root.dataset.v4pRestockLabel}: ${selectedVariant.dataset.v4pVariantTitle}`;
  };

  const focusVariantPicker = () => {
    const picker = root.querySelector('.variant-picker');
    picker?.scrollIntoView({ block: 'center', behavior: 'auto' });
    (variantButtons.find((button) => button.dataset.v4pVariantAvailable === 'true') || variantButtons[0])?.focus({ preventScroll: true });
  };

  const updateMainProductImage = ({ src, srcset, alt }) => {
    if (!mainProductImage || !src) return;
    if (srcset) mainProductImage.srcset = srcset;
    else mainProductImage.removeAttribute('srcset');
    mainProductImage.src = src;
    if (alt) mainProductImage.alt = alt;
  };

  const setActiveGalleryButton = (activeButton) => {
    root.querySelectorAll('[data-v4p-gallery-image]').forEach((button) => {
      const isActive = button === activeButton;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
    const count = root.querySelector('.pdp-gallery__count');
    if (count) count.hidden = !activeButton;
    if (!activeButton) return;
    if (count) count.textContent = `${activeButton.dataset.v4pGalleryIndex} / ${root.querySelectorAll('[data-v4p-gallery-image]').length}`;
    const strip = activeButton.parentElement;
    if (strip) strip.scrollLeft = activeButton.offsetLeft - strip.offsetLeft - (strip.clientWidth - activeButton.clientWidth) / 2;
  };

  const selectVariant = (button, updateUrl = true) => {
    if (!button || (productForm && pendingForms.has(productForm))) return;
    selectedVariant = button;
    selectionConfirmed = true;
    renderPurchase();
    variantInput?.dispatchEvent(new Event('change', { bubbles: true }));
    const error = root.querySelector('[data-v4p-purchase-error]');
    if (error) error.textContent = '';
    updateMainProductImage({
      src: button.dataset.v4pVariantImage,
      srcset: button.dataset.v4pVariantSrcset,
      alt: button.dataset.v4pVariantImageAlt,
    });
    const gallery = root.querySelector(`[data-v4p-gallery-image][data-v4p-media-id="${button.dataset.v4pMediaId}"]`);
    setActiveGalleryButton(gallery);
    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', button.dataset.v4pVariantId);
      url.searchParams.delete('option_values');
      window.history.replaceState(window.history.state, '', url);
    }
  };

  const openProductLightbox = () => {
    if (!productLightbox || !productLightboxImage || !mainProductImage) return;
    productLightboxImage.src = mainProductImage.src;
    productLightboxImage.srcset = mainProductImage.srcset;
    productLightboxImage.sizes = '100vw';
    productLightboxImage.alt = mainProductImage.alt;
    if (typeof productLightbox.showModal === 'function') productLightbox.showModal();
    else productLightbox.setAttribute('open', '');
  };

  const closeProductLightbox = () => {
    if (!productLightbox) return;
    if (typeof productLightbox.close === 'function' && productLightbox.open) productLightbox.close();
    else productLightbox.removeAttribute('open');
  };

  const closeMenu = () => {
    menu?.classList.remove('is-open');
    menu?.setAttribute('aria-hidden', 'true');
    backdrop?.classList.remove('is-visible');
    document.body.classList.remove('is-locked');
  };

  const openMenu = () => {
    menu?.classList.add('is-open');
    menu?.setAttribute('aria-hidden', 'false');
    backdrop?.classList.add('is-visible');
    document.body.classList.add('is-locked');
  };

  const closeFilters = () => {
    filterSheet?.classList.remove('is-open');
    filterSheet?.setAttribute('aria-hidden', 'true');
    backdrop?.classList.remove('is-visible');
    document.body.classList.remove('is-locked');
  };

  const openFilters = () => {
    filterSheet?.classList.add('is-open');
    filterSheet?.setAttribute('aria-hidden', 'false');
    backdrop?.classList.add('is-visible');
    document.body.classList.add('is-locked');
  };

  const showToast = (message) => {
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.setTimeout(() => toast.classList.remove('is-visible'), 2200);
  };

  const openThemeCart = () => {
    const trigger = document.querySelector('#header-group [ref="cartButton"], #header-group button[aria-label="Cart"], #header-group button[aria-label*="cart" i]');
    trigger?.click();
  };

  const addProduct = async (form) => {
    if (pendingForms.has(form)) return;
    if (form === productForm) {
      const state = purchaseState();
      if (state.needsChoice) { focusVariantPicker(); return; }
      if (!state.canPurchase) return;
    }
    const formData = new FormData(form);
    const id = formData.get('id');
    if (!id) return;
    const quantity = Number(formData.get('quantity')) || 1;
    const submit = form.querySelector('button[type="submit"]');
    if (submit?.disabled) return;
    pendingForms.add(form);
    let added = false;
    const errorMessage = form.querySelector('[data-v4p-purchase-error]');
    if (errorMessage) errorMessage.textContent = '';
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((component) => component.dataset.sectionId).filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    root.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(id), quantity }], promise: deferred.promise }));
    submit?.setAttribute('aria-busy', 'true');
    if (submit) submit.disabled = true;
    renderPurchase();

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity }], sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add item');
      added = true;
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(root.dataset.v4pCartRefreshLabel);
      const cart = await cartResponse.json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'v4-prototype-home', sourceId: form.id || 'v4-prototype-card', itemCount: quantity, sections: result.sections, didError: false } });
      root.querySelectorAll('.cart-count').forEach((count) => { count.textContent = cart.item_count; count.hidden = cart.item_count === 0; });
      if (form === productForm) showToast(root.dataset.v4pAddedLabel || Theme.translations.added);
    } catch (error) {
      deferred.reject(error);
      root.dispatchEvent(new CartErrorEvent({ error: error instanceof Error ? error.message : 'Unable to add item', code: 'INVALID' }));
      const message = added ? root.dataset.v4pCartRefreshLabel : (error instanceof Error ? error.message : root.dataset.v4pErrorLabel);
      if (errorMessage) errorMessage.textContent = message;
      showToast(message);
    } finally {
      pendingForms.delete(form);
      submit?.removeAttribute('aria-busy');
      if (submit) submit.disabled = false;
      renderPurchase();
    }
  };

  root.addEventListener('click', (event) => {
    const target = event.target.closest('button, a');
    if (!target) return;
    if (target.matches('[data-v4p-open-menu]')) openMenu();
    if (target.matches('[data-v4p-close-menu]') || target.closest('[data-v4p-mobile-menu] a')) closeMenu();
    if (target.matches('[data-v4p-open-filters]')) openFilters();
    if (target.matches('[data-v4p-close-filters]')) closeFilters();
    if (target.matches('[data-v4p-open-cart]')) openThemeCart();
    if (target.matches('[data-v4p-zoom-open]')) openProductLightbox();
    if (target.matches('[data-v4p-zoom-close]')) closeProductLightbox();

    const galleryButton = target.closest('[data-v4p-gallery-image]');
    if (galleryButton) {
      updateMainProductImage({
        src: galleryButton.dataset.v4pGalleryImage,
        srcset: galleryButton.dataset.v4pGallerySrcset,
        alt: galleryButton.querySelector('img')?.alt,
      });
      setActiveGalleryButton(galleryButton);
    }

    if (target.matches('[data-v4p-quantity-minus], [data-v4p-quantity-plus]') && quantityInput && !pendingForms.has(productForm)) {
      const step = Number(selectedVariant.dataset.v4pQuantityStep) || 1;
      const delta = target.matches('[data-v4p-quantity-plus]') ? step : -step;
      quantityInput.value = String(Number(quantityInput.value || 1) + delta);
      renderPurchase();
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const variantButton = target.closest('[data-v4p-variant-id]');
    if (variantButton) selectVariant(variantButton);

    if (target.matches('[data-v4p-sticky-submit]')) root.querySelector('.v4p-pdp-form')?.requestSubmit();
  });

  root.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.v4p-product-form')) return;
    event.preventDefault();
    addProduct(form);
  });

  root.addEventListener('change', (event) => {
    const select = event.target.closest('[data-v4p-sort]');
    if (select) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.assign(url.href);
    }
  });

  backdrop?.addEventListener('click', () => { closeMenu(); closeFilters(); });
  productLightbox?.addEventListener('click', (event) => {
    if (event.target === productLightbox || event.target.matches('.pdp-lightbox__surface')) closeProductLightbox();
  });
  productLightbox?.addEventListener('close', () => window.setTimeout(() => productZoomButton?.focus(), 0));
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') { closeMenu(); closeFilters(); } });
  if (productForm) {
    renderPurchase();
    window.addEventListener('popstate', () => {
      const id = new URL(window.location.href).searchParams.get('variant');
      const button = variantButtons.find((option) => option.dataset.v4pVariantId === id);
      if (button) selectVariant(button, false);
      else {
        selectionConfirmed = variantButtons.length <= 1;
        renderPurchase();
      }
    });
    window.addEventListener('pageshow', () => renderPurchase());
  }
}
